"use client";

import { c as _c } from "react/compiler-runtime";
import { trapTabFocus } from "@/components/utils/modal/focusTrap";
import { WaveDropCreate } from "@/components/waves/leaderboard/create/WaveDropCreate";
import type { ApiWave } from "@/generated/models/ApiWave";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
interface WaveChatSubmitDropModalProps {
  readonly isOpen: boolean;
  readonly wave: ApiWave;
  readonly title: string;
  readonly onClose: () => void;
  readonly initialCurationUrl?: string | null | undefined;
}
function isEventFromNestedAriaModal(event: KeyboardEvent, parentDialog: HTMLDialogElement | null) {
  if (!parentDialog || !(event.target instanceof Element)) {
    return false;
  }
  const nearestAriaModal = event.target.closest('[aria-modal="true"]');
  return nearestAriaModal !== null && nearestAriaModal !== parentDialog;
}
export function WaveChatSubmitDropModal(t0) {
  const $ = _c(34);
  const {
    isOpen,
    wave,
    title,
    onClose,
    initialCurationUrl: t1
  } = t0;
  const initialCurationUrl = t1 === undefined ? null : t1;
  const canUseDOM = typeof document !== "undefined";
  const titleId = useId();
  const dialogRef = useRef(null);
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);
  const titleRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const previouslyFocusedElementRef = useRef(null);
  let t2;
  let t3;
  if ($[0] !== onClose) {
    t2 = () => {
      onCloseRef.current = onClose;
    };
    t3 = [onClose];
    $[0] = onClose;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== isOpen) {
    t4 = () => {
      if (!isOpen) {
        return;
      }
      const activeElement = document.activeElement;
      previouslyFocusedElementRef.current = activeElement instanceof HTMLElement ? activeElement : null;
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const focusInitialTarget = () => {
        const initialTarget = closeButtonRef.current ?? titleRef.current ?? panelRef.current;
        initialTarget?.focus();
      };
      focusInitialTarget();
      const onKeyDown = event => {
        const panel = panelRef.current;
        if (!panel) {
          return;
        }
        if (event.key !== "Escape" && event.key !== "Tab") {
          return;
        }
        if (event.defaultPrevented) {
          return;
        }
        if (isEventFromNestedAriaModal(event, dialogRef.current)) {
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          onCloseRef.current();
          return;
        }
        trapTabFocus(event, panel);
      };
      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.body.style.overflow = originalOverflow;
        document.removeEventListener("keydown", onKeyDown);
        const previouslyFocusedElement = previouslyFocusedElementRef.current;
        if (previouslyFocusedElement?.isConnected) {
          previouslyFocusedElement.focus();
        }
        previouslyFocusedElementRef.current = null;
      };
    };
    t5 = [isOpen];
    $[3] = isOpen;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  if (!canUseDOM || !isOpen) {
    return null;
  }
  let t6;
  if ($[6] !== title) {
    t6 = title.toLowerCase();
    $[6] = title;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const closeBackdropLabel = `Close ${t6} modal`;
  let t7;
  if ($[8] !== closeBackdropLabel || $[9] !== onClose) {
    t7 = <button type="button" onClick={onClose} className="tw-fixed tw-inset-0 tw-cursor-default tw-border-0 tw-bg-transparent tw-p-0" aria-label={closeBackdropLabel} tabIndex={-1} />;
    $[8] = closeBackdropLabel;
    $[9] = onClose;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t10;
  let t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 0,
      y: 12,
      scale: 0.98
    };
    t9 = {
      opacity: 1,
      y: 0,
      scale: 1
    };
    t10 = {
      duration: 0.2,
      ease: "easeOut"
    };
    $[11] = t10;
    $[12] = t8;
    $[13] = t9;
  } else {
    t10 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t11;
  if ($[14] !== title || $[15] !== titleId) {
    t11 = <h2 id={titleId} ref={titleRef} className="tw-mb-0 tw-text-2xl tw-font-bold tw-text-white" tabIndex={-1}>{title}</h2>;
    $[14] = title;
    $[15] = titleId;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <XMarkIcon className="tw-size-6 tw-flex-shrink-0" />;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== onClose) {
    t13 = <button ref={closeButtonRef} type="button" onClick={onClose} className="tw-flex tw-size-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-iron-300 tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-iron-100" aria-label="Close modal">{t12}</button>;
    $[18] = onClose;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t11 || $[21] !== t13) {
    t14 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-solid tw-border-iron-800 tw-p-6">{t11}{t13}</div>;
    $[20] = t11;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== initialCurationUrl || $[24] !== onClose || $[25] !== wave) {
    t15 = <div className="tw-max-h-[calc(90vh-88px)] tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300"><div className="tw-px-6 tw-pb-6 tw-pt-5"><WaveDropCreate wave={wave} onCancel={onClose} onSuccess={onClose} onExitFixedDropMode={onClose} initialCurationUrl={initialCurationUrl} identityPickerPlacement="inline" isModalContent={true} /></div></div>;
    $[23] = initialCurationUrl;
    $[24] = onClose;
    $[25] = wave;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t14 || $[28] !== t15) {
    t16 = <LazyMotion features={domAnimation}><div className="tw-relative tw-z-10 tw-flex tw-h-full tw-items-start tw-justify-center tw-px-4 tw-pb-4 tw-pt-[calc(env(safe-area-inset-top,0px)+1rem)] lg:tw-items-center"><m.div ref={panelRef} initial={t8} animate={t9} transition={t10} className="tw-max-h-[90vh] tw-w-full tw-max-w-3xl tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-shadow-2xl" data-testid="chat-submit-drop-modal-panel" tabIndex={-1}>{t14}{t15}</m.div></div></LazyMotion>;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t16 || $[31] !== t7 || $[32] !== titleId) {
    t17 = createPortal(<dialog ref={dialogRef} open={true} aria-modal="true" aria-labelledby={titleId} className="tailwind-scope tw-fixed tw-inset-0 tw-z-[1000] tw-m-0 tw-h-full tw-w-full tw-max-w-none tw-border-0 tw-bg-gray-600/50 tw-p-0 tw-outline-none tw-backdrop-blur-[1px]" data-testid="chat-submit-drop-modal">{t7}{t16}</dialog>, document.body);
    $[30] = t16;
    $[31] = t7;
    $[32] = titleId;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  return t17;
}